import { useIntl } from 'react-intl';
import { CalendarViewMode } from '../utils';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import { Box, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CalendarViewMonthRoundedIcon from '@mui/icons-material/CalendarViewMonthRounded';

type ViewModeSwitcherProps = {
  value: CalendarViewMode;
  onChange: (value: CalendarViewMode) => void;
};

const ViewModeSwitcher = ({ value, onChange }: ViewModeSwitcherProps) => {
  const intl = useIntl();
  const options: Array<{
    value: CalendarViewMode;
    icon: JSX.Element;
    label: string;
    shortLabel: string;
  }> = [
    {
      value: 'calendarYear',
      icon: <CalendarViewMonthRoundedIcon fontSize="small" />,
      label: intl.formatMessage({ id: 'yearlyHoliday.view.calendarYear', defaultMessage: 'Takvim Yılı' }),
      shortLabel: intl.formatMessage({ id: 'yearlyHoliday.view.calendarYearShort', defaultMessage: 'Yıl' }),
    },
    {
      value: 'rollingYear',
      icon: <DateRangeRoundedIcon fontSize="small" />,
      label: intl.formatMessage({ id: 'yearlyHoliday.view.rollingYear', defaultMessage: '12 Aylık Dönem' }),
      shortLabel: intl.formatMessage({ id: 'yearlyHoliday.view.rollingYearShort', defaultMessage: '12 Ay' }),
    },
    {
      value: 'currentMonth',
      icon: <CalendarTodayRoundedIcon fontSize="small" />,
      label: intl.formatMessage({ id: 'yearlyHoliday.view.currentMonth', defaultMessage: 'Bu Ay' }),
      shortLabel: intl.formatMessage({ id: 'yearlyHoliday.view.currentMonthShort', defaultMessage: 'Bu Ay' }),
    },
  ];

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      value={value}
      onChange={(_, nextValue: CalendarViewMode | null) => {
        if (nextValue) onChange(nextValue);
      }}
      aria-label={intl.formatMessage({
        id: 'yearlyHoliday.viewSelectorAria',
        defaultMessage: 'Takvim görünümü',
      })}
      sx={{
        width: { xs: '100%', lg: 'auto' },
        p: 0.4,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.25,
        backgroundColor: 'background.default',
        boxShadow: 'none',
        '& .MuiToggleButtonGroup-grouped': {
          minHeight: 38,
          minWidth: { xs: 0, sm: 128 },
          px: { xs: 0.75, sm: 1.4 },
          gap: { xs: 0.5, sm: 0.75 },
          border: 0,
          borderRadius: '7px !important',
          color: 'text.secondary',
          fontSize: '0.78rem',
          fontWeight: 750,
          lineHeight: 1.2,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          boxShadow: 'none',
          '&.Mui-selected': {
            color: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
            backgroundColor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.13),
            boxShadow: (theme) => theme.shadows[1],
          },
          '&.Mui-selected:hover': {
            backgroundColor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.26 : 0.18),
          },
        },
      }}>
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.shortLabel === option.label ? option.label : `${option.shortLabel}; ${option.label}`}>
          {option.icon}
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {option.label}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {option.shortLabel}
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default ViewModeSwitcher;
